import { API, graphqlOperation } from 'aws-amplify';
import config from 'aws-exports';
import {
  listEmployees,
  getEmployee
} from 'graphql/queries';
import {
  createEmployee as createEmployeeMutation,
  updateEmployee as updateEmployeeMutation,
  createUser as createUserMutation,
  createCognitoUser as createCognitoUserMutation,
  deleteCognitoUser as deleteCognitoUserMutation,
  deleteEmployee as deleteEmployeeMutation
} from 'graphql/mutations';
import * as logger from 'utils/logger';
// Removed import of deleteUser - keeping Employee and User management separate

// Pagination configuration
const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 200;
const FETCH_ALL_BATCH_SIZE = 100;

/**
 * Fetches a single page of employees for a given client
 * @param {string} clientId - The client identifier to filter employees
 * @param {string|null} nextToken - Pagination token for next page
 * @param {number} pageSize - Number of items per page (max 200)
 * @returns {Promise<{items: Array, nextToken: string|null, hasMore: boolean}>} Paginated response
 * @throws {Error} If the GraphQL query fails
 */
export const fetchEmployeesPage = async (clientId, nextToken = null, pageSize = DEFAULT_PAGE_SIZE) => {
  // Parameter validation
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }
  if (nextToken !== null && typeof nextToken !== 'string') {
    throw new Error('nextToken must be a string or null');
  }
  if (typeof pageSize !== 'number' || pageSize < 1) {
    throw new Error('pageSize must be a positive number');
  }

  try {
    const filter = {
      clientId: { eq: clientId }
    };
    
    // Ensure pageSize doesn't exceed maximum
    const limit = Math.min(pageSize, MAX_PAGE_SIZE);
    
    const params = {
      filter,
      limit
    };
    
    if (nextToken) {
      params.nextToken = nextToken;
    }
    
    const result = await API.graphql(
      graphqlOperation(listEmployees, params)
    );
    
    return {
      items: result.data.listEmployees.items || [],
      nextToken: result.data.listEmployees.nextToken,
      hasMore: !!result.data.listEmployees.nextToken
    };
  } catch (error) {
    logger.error('Failed to fetch employees page', { clientId, error });
    throw new Error('Unable to fetch employees. Please try again.');
  }
};

/**
 * Fetches all employees for a client using pagination
 * @param {string} clientId - The client identifier to filter employees
 * @returns {Promise<Array>} Array of all employee records
 * @throws {Error} If the GraphQL query fails
 */
export const fetchEmployees = async (clientId) => {
  // Parameter validation
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    const allItems = [];
    let nextToken = null;
    let fetchCount = 0;
    const maxFetches = 50; // Safety limit to prevent infinite loops
    
    do {
      const response = await fetchEmployeesPage(clientId, nextToken, FETCH_ALL_BATCH_SIZE);
      allItems.push(...response.items);
      nextToken = response.nextToken;
      fetchCount++;
      
      // Safety check to prevent infinite loops
      if (fetchCount >= maxFetches) {
        logger.warn('Reached maximum fetch limit while fetching employees', { 
          maxFetches, 
          fetchCount, 
          itemsFetched: allItems.length 
        });
        break;
      }
    } while (nextToken);
    
    logger.debug('Successfully fetched all employees', { 
      totalItems: allItems.length, 
      batchCount: fetchCount 
    });
    return allItems;
  } catch (error) {
    logger.error('Failed to fetch all employees', { clientId, error });
    throw new Error('Unable to fetch employees. Please try again.');
  }
};

/**
 * Fetches all employees with their assignment counts by hierarchy level
 * @param {string} clientId - The client identifier to filter employees
 * @returns {Promise<Array>} Array of employee records with assignment counts
 * @throws {Error} If the GraphQL query fails
 */
export const fetchEmployeesWithAssignmentCounts = async (clientId) => {
  // Parameter validation
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    // First fetch all employees
    const employees = await fetchEmployees(clientId);
    
    // Import assignment service functions
    const { fetchLocationAssignments, fetchOrgUnitAssignments } = await import('./assignmentService');
    
    // Fetch assignments for each employee and calculate counts
    const employeesWithCounts = await Promise.all(
      employees.map(async (employee) => {
        try {
          // Fetch both location and org unit assignments
          const [locationAssignments, orgUnitAssignments] = await Promise.all([
            fetchLocationAssignments(employee.id, clientId),
            fetchOrgUnitAssignments(employee.id, clientId)
          ]);
          
          // Combine all assignments
          const allAssignments = [...locationAssignments, ...orgUnitAssignments];
          
          // Calculate counts by type and org level
          const assignmentsByLevel = {};
          let locationCount = 0;
          
          // Count location assignments
          locationAssignments.forEach(() => {
            locationCount++;
          });
          
          // Count org unit assignments by level
          orgUnitAssignments.forEach(assignment => {
            const orgLevel = assignment.orgLevel || 'ORGLEVEL_1';
            assignmentsByLevel[orgLevel] = (assignmentsByLevel[orgLevel] || 0) + 1;
          });
          
          // Add location count to the assignmentsByLevel object
          assignmentsByLevel.locations = locationCount;
          
          return {
            ...employee,
            assignments: allAssignments,
            assignmentsByLevel,
            totalAssignments: allAssignments.length
          };
        } catch (error) {
          logger.warn('Failed to fetch assignments for employee', { 
            employeeId: employee.id, 
            error: error.message 
          });
          return {
            ...employee,
            assignments: [],
            assignmentsByLevel: {},
            totalAssignments: 0
          };
        }
      })
    );
    
    logger.debug('Successfully fetched employees with assignment counts', { 
      totalEmployees: employeesWithCounts.length 
    });
    
    return employeesWithCounts;
  } catch (error) {
    logger.error('Failed to fetch employees with assignment counts', { clientId, error });
    throw new Error('Unable to fetch employees with assignment counts. Please try again.');
  }
};

/**
 * Fetches a single employee by their ID
 * @param {string} id - The employee ID
 * @returns {Promise<Object>} Employee record
 * @throws {Error} If the GraphQL query fails
 */
export const fetchEmployee = async (id) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid employee ID is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(getEmployee, { id })
    );
    
    return result.data.getEmployee;
  } catch (error) {
    logger.error('Failed to fetch employee', { id, error });
    throw new Error('Unable to fetch employee details. Please try again.');
  }
};

/**
 * Creates a new employee record with optional Cognito user creation
 * @param {Object} employeeData - Employee data including firstName, lastName, email, etc.
 * @param {boolean} employeeData.grantSystemAccess - Whether to create a RadixDash user account
 * @param {string} employeeData.userName - Username for RadixDash account (required if grantSystemAccess=true)
 * @param {string} employeeData.userRole - User role (required if grantSystemAccess=true)
 * @returns {Promise<Object>} Created employee record (with userId if user was created)
 * @throws {Error} If the GraphQL mutation fails
 */
export const createEmployee = async (employeeData) => {
  // Parameter validation
  if (!employeeData || typeof employeeData !== 'object') {
    throw new Error('Employee data is required');
  }
  if (!employeeData.firstName || typeof employeeData.firstName !== 'string') {
    throw new Error('Valid firstName is required');
  }
  if (!employeeData.lastName || typeof employeeData.lastName !== 'string') {
    throw new Error('Valid lastName is required');
  }
  if (!employeeData.email || typeof employeeData.email !== 'string') {
    throw new Error('Valid email is required');
  }
  if (!employeeData.clientId || typeof employeeData.clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  // Validate RadixDash access fields if granting access
  if (employeeData.grantSystemAccess) {
    if (!employeeData.userName || typeof employeeData.userName !== 'string') {
      throw new Error('Valid userName is required when granting system access');
    }
    if (!employeeData.userRole || typeof employeeData.userRole !== 'string') {
      throw new Error('Valid userRole is required when granting system access');
    }
  }

  let createdEmployee = null;
  let createdCognitoUser = false;
  let createdDbUser = null;

  try {
    // Step 1: Create Employee record
    const formattedData = { ...employeeData };

    // Remove RadixDash access fields from employee data
    delete formattedData.grantSystemAccess;
    delete formattedData.userName;
    delete formattedData.userRole;

    if (formattedData.hireDate) {
      if (formattedData.hireDate.includes('T')) {
        formattedData.hireDate = formattedData.hireDate.split('T')[0];
      }
    } else if (formattedData.hireDate === '') {
      delete formattedData.hireDate;
    }

    if (formattedData.terminationDate) {
      if (formattedData.terminationDate.includes('T')) {
        formattedData.terminationDate = formattedData.terminationDate.split('T')[0];
      }
    } else if (formattedData.terminationDate === '') {
      delete formattedData.terminationDate;
    }

    const employeeInput = {
      ...formattedData,
      isActive: formattedData.isActive !== false,
      lastModified: new Date().toISOString()
    };

    const employeeResult = await API.graphql(
      graphqlOperation(createEmployeeMutation, { input: employeeInput })
    );

    createdEmployee = employeeResult.data.createEmployee;
    logger.info('Successfully created employee', { employeeId: createdEmployee.id });

    // Step 2: Create Cognito user and database User record if requested
    if (employeeData.grantSystemAccess) {
      try {
        // Step 2a: Create Cognito user (auto-generates and emails temp password)
        logger.info('Creating Cognito user', { userName: employeeData.userName });
        const cognitoResponse = await API.graphql({
          query: createCognitoUserMutation,
          variables: {
            userPool: config.aws_user_pools_id,
            userName: employeeData.userName.toLowerCase(),
            email: employeeData.email
            // No tempPassword parameter = Cognito auto-generates and emails it
          }
        });

        createdCognitoUser = true;
        logger.info('Successfully created Cognito user', { userName: employeeData.userName });

        // Step 2b: Create database User record with employeeId cross-link
        try {
          const userInput = {
            client: employeeData.clientId,
            userName: employeeData.userName.toLowerCase(),
            firstName: employeeData.firstName,
            lastName: employeeData.lastName,
            email: employeeData.email,
            groupRole: employeeData.userRole.toUpperCase(), // Admin -> ADMIN, Manager -> MANAGER, User -> USER
            employeeId: createdEmployee.id, // Cross-link to Employee
            status: 'ACTIVE',
            securityVersion: 1
          };

          const userResult = await API.graphql({
            query: createUserMutation,
            variables: { input: userInput }
          });

          createdDbUser = userResult.data.createUser;
          logger.info('Successfully created database user', { userId: createdDbUser.id });

          // Step 2c: Update Employee record with userId cross-link
          try {
            const updateInput = {
              id: createdEmployee.id,
              userId: createdDbUser.id, // Cross-link to User
              lastModified: new Date().toISOString()
            };

            const updateResult = await API.graphql(
              graphqlOperation(updateEmployeeMutation, { input: updateInput })
            );

            createdEmployee = updateResult.data.updateEmployee;
            logger.info('Successfully linked employee to user', {
              employeeId: createdEmployee.id,
              userId: createdDbUser.id
            });

          } catch (linkError) {
            logger.error('Failed to link employee to user', {
              employeeId: createdEmployee.id,
              userId: createdDbUser.id,
              error: linkError
            });

            // Rollback: Delete User and Cognito user
            logger.info('Rolling back - deleting database user');
            // Note: We don't have a deleteUser mutation, so we'll deactivate instead
            // The Cognito user will be deleted below

            throw new Error('Failed to link employee to user account');
          }

        } catch (dbUserError) {
          logger.error('Failed to create database user', { error: dbUserError });

          // Rollback: Delete Cognito user
          logger.info('Rolling back - deleting Cognito user', { userName: employeeData.userName });
          try {
            await API.graphql({
              query: deleteCognitoUserMutation,
              variables: {
                userPool: config.aws_user_pools_id,
                userName: employeeData.userName.toLowerCase()
              }
            });
            logger.info('Successfully rolled back Cognito user');
          } catch (rollbackError) {
            logger.error('Error rolling back Cognito user', { error: rollbackError });
          }

          throw new Error('Failed to create user account in database');
        }

      } catch (cognitoError) {
        logger.error('Failed in user creation process', { error: cognitoError });

        // Rollback: Delete Employee record
        logger.info('Rolling back - deleting employee', { employeeId: createdEmployee.id });
        try {
          await API.graphql({
            query: deleteEmployeeMutation,
            variables: { input: { id: createdEmployee.id } }
          });
          logger.info('Successfully rolled back employee record');
        } catch (rollbackError) {
          logger.error('Error rolling back employee', { error: rollbackError });
        }

        // If Cognito user was created, delete it
        if (createdCognitoUser) {
          logger.info('Rolling back - deleting Cognito user', { userName: employeeData.userName });
          try {
            await API.graphql({
              query: deleteCognitoUserMutation,
              variables: {
                userPool: config.aws_user_pools_id,
                userName: employeeData.userName.toLowerCase()
              }
            });
            logger.info('Successfully rolled back Cognito user');
          } catch (rollbackError) {
            logger.error('Error rolling back Cognito user', { error: rollbackError });
          }
        }

        throw new Error(`Failed to create user account: ${cognitoError.message}`);
      }
    }

    return createdEmployee;

  } catch (error) {
    logger.error('Failed to create employee', {
      email: employeeData.email,
      clientId: employeeData.clientId,
      error
    });

    // If this is our own error, rethrow it
    if (error.message.includes('Failed to')) {
      throw error;
    }

    throw new Error('Unable to create employee. Please try again.');
  }
};

/**
 * Updates an existing employee record
 * @param {string} id - The employee ID to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated employee record
 * @throws {Error} If the GraphQL mutation fails
 */
export const updateEmployee = async (id, updates) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid employee ID is required');
  }
  if (!updates || typeof updates !== 'object') {
    throw new Error('Updates object is required');
  }

  try {
    // Format date fields if present
    const formattedUpdates = { ...updates };
    
    // Handle hireDate formatting - AWSDate expects YYYY-MM-DD format
    if (formattedUpdates.hireDate) {
      if (formattedUpdates.hireDate.length === 10) {
        // Already in YYYY-MM-DD format, keep as is
        // formattedUpdates.hireDate stays as is
      } else if (formattedUpdates.hireDate.includes('T')) {
        // If it's an ISO string, extract just the date part
        formattedUpdates.hireDate = formattedUpdates.hireDate.split('T')[0];
      }
    } else if (formattedUpdates.hireDate === '') {
      // Remove empty date field to avoid validation errors
      delete formattedUpdates.hireDate;
    }
    
    // Handle terminationDate formatting - AWSDate expects YYYY-MM-DD format
    if (formattedUpdates.terminationDate) {
      if (formattedUpdates.terminationDate.length === 10) {
        // Already in YYYY-MM-DD format, keep as is
        // formattedUpdates.terminationDate stays as is
      } else if (formattedUpdates.terminationDate.includes('T')) {
        // If it's an ISO string, extract just the date part
        formattedUpdates.terminationDate = formattedUpdates.terminationDate.split('T')[0];
      }
    } else if (formattedUpdates.terminationDate === '') {
      // Remove empty date field to avoid validation errors
      delete formattedUpdates.terminationDate;
    }
    
    const input = {
      id,
      ...formattedUpdates,
      lastModified: new Date().toISOString()
    };
    
    // Log the mutation input for debugging
    logger.info('Updating employee with input:', { input });
    
    const result = await API.graphql(
      graphqlOperation(updateEmployeeMutation, { input })
    );
    
    return result.data.updateEmployee;
  } catch (error) {
    logger.error('Failed to update employee', { id, updates, error });
    throw new Error('Unable to update employee. Please try again.');
  }
};

/**
 * Deactivates an employee (soft delete)
 * @param {string} id - The employee ID to deactivate
 * @returns {Promise<Object>} Updated employee record with isActive=false
 * @throws {Error} If the GraphQL mutation fails
 */
export const deactivateEmployee = async (id) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid employee ID is required');
  }

  try {
    const input = {
      id,
      isActive: false,
      terminationDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD format for AWSDate
      lastModified: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateEmployeeMutation, { input })
    );
    
    return result.data.updateEmployee;
  } catch (error) {
    logger.error('Failed to deactivate employee', { id, error });
    throw new Error('Unable to deactivate employee. Please try again.');
  }
};

/**
 * Reactivates a previously deactivated employee
 * @param {string} id - The employee ID to reactivate
 * @returns {Promise<Object>} Updated employee record with isActive=true
 * @throws {Error} If the GraphQL mutation fails
 */
export const reactivateEmployee = async (id) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid employee ID is required');
  }

  try {
    const input = {
      id,
      isActive: true,
      terminationDate: null,
      lastModified: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateEmployeeMutation, { input })
    );
    
    return result.data.updateEmployee;
  } catch (error) {
    logger.error('Failed to reactivate employee', { id, error });
    throw new Error('Unable to reactivate employee. Please try again.');
  }
};

/**
 * Checks if an email address is unique within a client
 * @param {string} email - The email address to check
 * @param {string} clientId - The client identifier
 * @param {string|null} excludeId - Employee ID to exclude from check (for updates)
 * @returns {Promise<boolean>} True if email is unique, false otherwise
 */
export const checkEmailUniqueness = async (email, clientId, excludeId = null) => {
  // Parameter validation
  if (!email || typeof email !== 'string') {
    throw new Error('Valid email is required');
  }
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }
  if (excludeId !== null && typeof excludeId !== 'string') {
    throw new Error('excludeId must be a string or null');
  }

  try {
    const filter = {
      clientId: { eq: clientId },
      email: { eq: email }
    };
    
    if (excludeId) {
      filter.id = { ne: excludeId };
    }
    
    const result = await API.graphql(
      graphqlOperation(listEmployees, { 
        filter,
        limit: 1
      })
    );
    
    return result.data.listEmployees.items.length === 0;
  } catch (error) {
    logger.error('Failed to check email uniqueness', { email, clientId, error });
    return false;
  }
};

/**
 * Links an employee record to a user account
 * @param {string} employeeId - The employee ID
 * @param {string} userId - The user ID to link
 * @returns {Promise<Object>} Updated employee record with userId
 * @throws {Error} If the GraphQL mutation fails
 */
export const linkEmployeeToUser = async (employeeId, userId) => {
  // Parameter validation
  if (!employeeId || typeof employeeId !== 'string') {
    throw new Error('Valid employee ID is required');
  }
  if (!userId || typeof userId !== 'string') {
    throw new Error('Valid user ID is required');
  }

  try {
    const input = {
      id: employeeId,
      userId,
      lastModified: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateEmployeeMutation, { input })
    );
    
    return result.data.updateEmployee;
  } catch (error) {
    logger.error('Failed to link employee to user', { employeeId, userId, error });
    throw new Error('Unable to link employee to user. Please try again.');
  }
};

/**
 * Unlinks an employee record from their user account
 * @param {string} employeeId - The employee ID to unlink
 * @returns {Promise<Object>} Updated employee record with userId=null
 * @throws {Error} If the GraphQL mutation fails
 */
export const unlinkEmployeeFromUser = async (employeeId) => {
  // Parameter validation
  if (!employeeId || typeof employeeId !== 'string') {
    throw new Error('Valid employee ID is required');
  }

  try {
    const input = {
      id: employeeId,
      userId: null,
      lastModified: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateEmployeeMutation, { input })
    );
    
    return result.data.updateEmployee;
  } catch (error) {
    logger.error('Failed to unlink employee from user', { employeeId, error });
    throw new Error('Unable to unlink employee from user. Please try again.');
  }
};

